import React, { useEffect, useRef, useState, useCallback } from "react";
import { MemberList } from "./MemberListPresenter";
import { useMutation } from "@apollo/client";
import {
  CREATE_USER,
  DELETE_USER,
  GET_All_USER_DATA,
  REVISE_USERBALANCE,
  REVISE_USER_SETTING,
} from "../../../graphql/manageUser";
import { useUserState } from "../../../../context";
import { toast } from "sonner";
import { getUserData, writeUserData } from "../../../cache/userData";
import { USER_DATA } from "../../../cache/userDataGql";
import MemberListForm from "../MemberOrderHistory/MemberListForm";
import { mainClient } from "../../../../apollo";
import exportExcel from "../../../common/exportExcel";
import dayjs from "dayjs";
import { getBankName } from "../../../common/data";
import { useSubscriptionEvent } from "../../../../utils/subscriptionEvents";

const initialValues = {
  id: "",
  userName: "",
  userPw: "",
  phoneNo: "",
  recommendCode: "",
  email: "",
  birthDate: "",
  userState: 1,
  authority: 0,
  userAccountBank: 0,
  userAccountNo: "",
  userAccountOwner: "",
  deposit_accountId: 1,
  memo: "",
};

const refetchUserData = async () => {
  const {
    data: { getAllUserData },
  } = await mainClient.query({
    query: GET_All_USER_DATA,
    fetchPolicy: "no-cache",
    variables: {
      isAll: true,
      withTester: true,
    },
  });
  writeUserData(getAllUserData);
  return getUserData(1);
};

const applyFilter = (data, showTesterOnly, excludeTester) => {
  if (showTesterOnly) return getUserData(0);
  if (excludeTester) return data.filter((user) => user.authority !== 4);
  return data;
};

export default () => {
  const [usersData, setUsersData] = useState(getUserData(1));
  const [isOpen, setIsOpen] = useState(false);
  const [isAddNewMember, setIsAddNewMember] = useState(false);
  const [formValues, setFormValues] = useState(initialValues);
  const [originalValues, setOriginalValues] = useState(initialValues);
  const [showTesterOnly, setShowTesterOnly] = useState(false);
  const [excludeTester, setExcludeTester] = useState(false);

  const {
    user: { userId: managerId },
  } = useUserState();

  const showMessage = useCallback((type, content) => {
    type === "error" ? toast.error(content) : toast.success(content);
  }, []);

  useEffect(() => {
    const loadFreshData = async () => {
      try {
        const refreshedData = await refetchUserData();
        setUsersData(applyFilter(refreshedData, showTesterOnly, excludeTester));
      } catch (error) {
        console.error("회원 데이터 로딩 실패:", error);
      }
    };
    loadFreshData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAndApplyFilter = useCallback(async () => {
    const refreshedData = await refetchUserData();
    setUsersData(applyFilter(refreshedData, showTesterOnly, excludeTester));
    return refreshedData;
  }, [showTesterOnly, excludeTester]);

  const [reviseUserSetting] = useMutation(REVISE_USER_SETTING, {
    update: (cache, { data }) => {
      const updated = data?.reviseUserSetting;
      if (!updated) return;

      const existing = cache.readFragment({
        id: `USERDATA:${updated.id}`,
        fragment: USER_DATA,
      });
      if (!existing) return;

      const merged = {
        ...existing,
        ...updated,
        // 블랙 처리(userState=0)는 ForceQuit publish가 확정되므로 연결 상태를 즉시 false로 반영
        connectState:
          updated.userState === 0 ? false : updated.connectState ?? existing.connectState,
        __typename: "UserData",
      };

      cache.writeFragment({
        id: `USERDATA:${updated.id}`,
        fragment: USER_DATA,
        data: merged,
      });
    },
    onCompleted: (result) => {
      const { reviseUserSetting } = result;
      showMessage(
        "success",
        `${reviseUserSetting.id}(${reviseUserSetting.userName}) 회원정보 수정 완료`,
      );
      setUsersData(applyFilter(getUserData(1), showTesterOnly, excludeTester));
      setIsOpen(false);
    },
    onError: (error) => {
      showMessage("error", `회원정보 수정 실패: ${error.message}`);
    },
  });

  const [reviseUserBalance] = useMutation(REVISE_USERBALANCE, {
    onCompleted: async ({ reviseUserBalance }) => {
      if (reviseUserBalance === 200) {
        // 뮤테이션은 스칼라 200만 반환하므로 캐시 자동 갱신이 안 된다.
        // 최신 회원 데이터를 다시 불러와 목록과 모달의 '현재 잔고'를 즉시 반영한다.
        await refreshAndApplyFilter();
        showMessage("success", "관리자 입출금완료");
      } else {
        showMessage("error", "관리자 입출금실패");
      }
    },
    onError: (error) => {
      showMessage("error", `관리자 입출금 실패: ${error.message}`);
    },
  });

  const [managerCreateUser] = useMutation(CREATE_USER, {
    onCompleted: async ({ createUserByManager }) => {
      if (createUserByManager.code === 200) {
        await refreshAndApplyFilter();
        setIsOpen(false);
        return showMessage("success", `${createUserByManager.data.id} 회원등록 완료`);
      }
      if (createUserByManager.code === 409) {
        return showMessage("error", "이미 존재하는 아이디입니다.");
      }
      return showMessage("error", "오류로 아이디를 생성할 수 없습니다.");
    },
    onError: (error) => {
      showMessage("error", `회원등록 실패: ${error.message}`);
    },
  });

  const [deleteUserMutation] = useMutation(DELETE_USER, {
    onCompleted: async ({ deleteUser }) => {
      if (deleteUser === 200) {
        showMessage("success", "회원이 삭제되었습니다.");
        await refreshAndApplyFilter();
      } else {
        showMessage("error", "회원 삭제에 실패했습니다.");
      }
      setIsOpen(false);
    },
    onError: (error) => {
      showMessage("error", `회원 삭제 실패: ${error.message}`);
    },
  });

  // ref로 최신 state 유지 (스테일 클로저 방지)
  const isOpenRef = useRef(isOpen);
  const formValuesRef = useRef(formValues);
  isOpenRef.current = isOpen;
  formValuesRef.current = formValues;

  useSubscriptionEvent("currentState", (targetData) => {
    if (targetData?.id) {
      setUsersData((prev) =>
        prev.map((user) =>
          user.id === targetData.id ? { ...user, ...targetData } : user
        )
      );

      // Form Drawer가 열려있고, 현재 편집 중인 유저의 데이터일 때만 폼 업데이트
      if (isOpenRef.current && formValuesRef.current.id === targetData.id) {
        setFormValues((prev) => ({ ...prev, ...targetData }));
      }
    }
  });

  const handleShowTesterOnlyChange = (checked) => {
    setShowTesterOnly(checked);
    if (checked) {
      setExcludeTester(false);
      setUsersData(getUserData(0));
    } else {
      setUsersData(getUserData(1));
    }
  };

  const handleExcludeTesterChange = (checked) => {
    setExcludeTester(checked);
    if (checked) {
      setShowTesterOnly(false);
      const allUsers = getUserData(1);
      setUsersData(allUsers.filter((user) => user.authority !== 4));
    } else {
      setUsersData(getUserData(1));
    }
  };

  class InvalidBirthDateError extends Error {
    constructor() {
      super("정확한 생년월일을 기입해주세요");
      this.name = "InvalidBirthDateError";
    }
  }

  // 8자리 YYYYMMDD 또는 ISO YYYY-MM-DD 둘 다 허용. 비어있으면 null. 실제
  // 날짜 유효성(2월 30일 등)은 mainServer에서 검증하므로 여기서는 형식만 본다.
  const normalizeBirthDate = (raw) => {
    if (raw === undefined || raw === null) return null;
    const trimmed = String(raw).trim();
    if (trimmed === "") return null;
    if (/^\d{8}$/.test(trimmed)) {
      return `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(6, 8)}`;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    throw new InvalidBirthDateError();
  };

  const handleOnFinish = (data) => {
    if (!isAddNewMember) {
      let revisedData = {};
      for (let key in data) {
        if (
          data.hasOwnProperty(key) &&
          originalValues.hasOwnProperty(key) &&
          data[key] !== originalValues[key]
        ) {
          revisedData[key] = data[key];
        }
      }
      if (Object.keys(revisedData).length === 0) {
        showMessage("error", "변경된 항목이 없습니다.");
        return;
      }
      if ("birthDate" in revisedData) {
        try {
          revisedData.birthDate = normalizeBirthDate(revisedData.birthDate);
        } catch (err) {
          if (err instanceof InvalidBirthDateError) {
            showMessage("error", err.message);
            return;
          }
          throw err;
        }
      }
      reviseUserSetting({
        variables: { ...revisedData, managerId, userId: data.id },
      });
    } else {
      let isoBirthDate;
      try {
        isoBirthDate = normalizeBirthDate(data.birthDate);
      } catch (err) {
        if (err instanceof InvalidBirthDateError) {
          showMessage("error", err.message);
          return;
        }
        throw err;
      }
      managerCreateUser({
        variables: {
          ...data,
          birthDate: isoBirthDate,
        },
      });
    }
  };

  const handleInquiry = (inquiryType, inquiryValue) => {
    let users = getUserData(1);
    if (showTesterOnly) {
      users = getUserData(0);
    } else if (excludeTester) {
      users = users.filter((user) => user.authority !== 4);
    }

    const fieldMap = {
      0: "id",
      1: "userName",
      2: "phoneNo",
      3: "email",
      4: "recommendCode",
      5: "connectIP",
      6: "connectMAC",
    };

    const field = fieldMap[inquiryType];
    if (!field) return;

    const filtered =
      inquiryType === 4
        ? users.filter((user) => user[field] && user[field] === inquiryValue)
        : users.filter(
            (user) => user[field] && user[field].includes(inquiryValue)
          );

    setUsersData(filtered);
  };

  const handleOnClick = (data) => {
    const formData = {
      ...data,
      optionSellCount: Math.floor(data.option_maxAvailableCount / 5),
    };
    setOriginalValues(formData);
    setFormValues(formData);
    setIsAddNewMember(false);
    setIsOpen(true);
  };

  const handleDeleteUser = (userId) => {
    deleteUserMutation({ variables: { managerId, userId } });
  };

  const handleExporting = (sortedData) => {
    const source = sortedData || usersData;
    const convertedData = source.map((user) => {
      const {
        createdAt, lastLogin, lastTrade, userState,
        deposit_accountBank, userAccountBank,
        isOverDomestic, updatedData_overDomestic,
        isOverOption, updatedData_overOption,
        isOverForeign, updatedData_overForeign,
        isOverHangSeng, updatedData_overHangseng,
        isOverMiniForeign, updatedData_miniForeign,
      } = user;

      let authority = "준회원";
      if (user.authority === 1) authority = "정회원";
      if (user.authority === 2) authority = "추천인";
      if (user.authority === 3) authority = "관리자";
      if (user.authority === 4) authority = "테스터";
      if (user.authority === 5) authority = "모의투자";

      return {
        ...user,
        createdAt: createdAt ? dayjs(createdAt).format("YYYY-MM-DD HH:mm:ss") : null,
        lastLogin: lastLogin ? dayjs(lastLogin).format("YYYY-MM-DD HH:mm:ss") : null,
        lastTrade: lastTrade ? dayjs(lastTrade).format("YYYY-MM-DD HH:mm:ss") : null,
        authority,
        userState: userState === 1 ? "정상" : "블랙",
        deposit_accountBank: getBankName(deposit_accountBank),
        userAccountBank: getBankName(userAccountBank),
        isOverDomestic: isOverDomestic ? "허용" : "불가",
        isOverOption: isOverOption ? "허용" : "불가",
        isOverForeign: isOverForeign ? "허용" : "불가",
        isOverHangSeng: isOverHangSeng ? "허용" : "불가",
        isOverMiniForeign: isOverMiniForeign ? "허용" : "불가",
        updatedData_overDomestic: updatedData_overDomestic ? dayjs(updatedData_overDomestic).format("YYYY-MM-DD HH:mm:ss") : null,
        updatedData_overOption: updatedData_overOption ? dayjs(updatedData_overOption).format("YYYY-MM-DD HH:mm:ss") : null,
        updatedData_overForeign: updatedData_overForeign ? dayjs(updatedData_overForeign).format("YYYY-MM-DD HH:mm:ss") : null,
        updatedData_overHangseng: updatedData_overHangseng ? dayjs(updatedData_overHangseng).format("YYYY-MM-DD HH:mm:ss") : null,
        updatedData_miniForeign: updatedData_miniForeign ? dayjs(updatedData_miniForeign).format("YYYY-MM-DD HH:mm:ss") : null,
      };
    });
    exportExcel("회원목록", convertedData);
  };

  const handleAddNewMember = () => {
    setFormValues(initialValues);
    setIsAddNewMember(true);
    setIsOpen(true);
  };

  return (
    <div className="h-full flex flex-col">
      <MemberListForm
        values={formValues}
        onChange={setFormValues}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleOnFinish={handleOnFinish}
        isAddNewMember={isAddNewMember}
        managerId={managerId}
        reviseUserBalance={reviseUserBalance}
        onDeleteUser={handleDeleteUser}
      />
      <MemberList
        dataSource={usersData}
        totalCount={usersData.length}
        handleOnClick={handleOnClick}
        onAddNewMember={handleAddNewMember}
        showTesterOnly={showTesterOnly}
        onShowTesterOnlyChange={handleShowTesterOnlyChange}
        excludeTester={excludeTester}
        onExcludeTesterChange={handleExcludeTesterChange}
        onInquiry={handleInquiry}
        onExport={handleExporting}
        managerId={managerId}
        reviseUserBalance={reviseUserBalance}
      />
    </div>
  );
};
